/**
 * Juste après l'inscription : « Ajoutez une photo pour qu'on vous reconnaisse ».
 * Un écran, une question, une action — et « plus tard » toujours possible.
 * Tom (2026-09-17) : on doit y être encouragé, pas contraint.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { envoyer } from '../../reseau.ts';
import { ChoixPhoto } from '../../composants/ChoixPhoto.tsx';
import { nomDe, type Espace } from './types.ts';

export function Photo({ espace, onChange }: { espace: Espace; onChange: () => Promise<void> }) {
  const naviguer = useNavigate();
  const [photo, setPhoto] = useState<string | null>(espace.profil?.photo_url ?? null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const suite = new URLSearchParams(location.search).get('suite') || '/compte/entreprise';

  const continuer = async (enregistrer: boolean) => {
    if (enregistrer && photo && photo !== espace.profil?.photo_url) {
      setEnCours(true);
      setErreur(null);
      const r = await envoyer('/api/compte/moi', { photoUrl: photo }, 'PUT');
      setEnCours(false);
      if (r.erreur) return setErreur(r.erreur);
      await onChange();
    }
    if (suite.startsWith('http')) location.assign(suite);
    else naviguer(suite, { replace: true });
  };

  return (
    <div className="mx-auto max-w-[520px] py-6 text-center">
      <p className="label-caps text-fumee">Bienvenue{espace.identite.prenom ? `, ${espace.identite.prenom}` : ''}</p>
      <h1 className="display-title mt-3 text-[30px] text-blanc sm:text-[38px]">
        Ajoutez une <em className="text-match-gradient font-serif italic font-medium">photo</em>
      </h1>
      <p className="mx-auto mt-3 max-w-[420px] text-[15px] leading-relaxed text-fumee">
        Pour qu’on vous reconnaisse facilement le jour de l’événement — un selfie suffit. Elle apparaît
        sur votre profil et votre page de rendez-vous.
      </p>

      <div className="mt-8 flex justify-center">
        <ChoixPhoto valeur={photo} onChange={setPhoto} nom={nomDe(espace.identite)} grand />
      </div>

      {erreur && <p className="mt-4 text-[13px]" style={{ color: 'var(--em-erreur)' }}>{erreur}</p>}

      <div className="mt-10 flex flex-col items-center gap-3">
        <button onClick={() => void continuer(true)} disabled={enCours || !photo} className="btn-creme !min-h-0 px-6 py-2.5 text-[15px] disabled:opacity-40">
          {enCours ? '…' : 'Continuer avec cette photo'}
        </button>
        <button onClick={() => void continuer(false)} className="text-[14px] text-fumee hover:text-blanc">
          Plus tard
        </button>
      </div>
    </div>
  );
}
